import { useCallback, useMemo, useState } from 'react';
import { localizeDate } from '@elementary/common-ui/utils/datetime';
import { getTestExecutionTotals } from 'shared/utils/testExecutions';
import { useCSVExport } from 'shared/hooks/useCSVExport';
import useGraphqlPagedQuery from '@elementary/common-ui/hooks/useGraphqlPagedQuery';
import { CATALOG_TABLE_ASSETS } from '@elementary/common-ui/constants/queryKeys';
import { EXPORT_PAGE_SIZE, PAGE_SIZE } from '@elementary/common-ui/constants';
import {
  CatalogTableAssetPagedResponse,
  getCatalogTableAssetsQuery,
} from '../api';
import { CatalogTableAssetData, CatalogTableAssetResponseItem } from '../types';
import { parseAssets } from '../utils';
import { useCatalogTableQueryContext } from './useCatalogTableQueryContext';

const CatalogCsvHeaders: {
  id: keyof CatalogTableAssetData | 'lastRunAt' | 'testResults';
  title: string;
}[] = [
  { id: 'dbName', title: 'Database' },
  { id: 'schemaName', title: 'Schema' },
  { id: 'name', title: 'Name' },
  { id: 'isCritical', title: 'Is Critical Asset' },
  { id: 'assetType', title: 'Type' },
  { id: 'description', title: 'Description' },
  { id: 'owners', title: 'Owners' },
  { id: 'tags', title: 'Tags' },
  { id: 'path', title: 'Path' },
  { id: 'lastRunAt', title: 'Last Run At' },
  { id: 'testResults', title: 'Test Results' },
];

type Props = {
  search?: string;
  enabled?: boolean;
};

export const useCatalogTableData = ({ search, enabled = true }: Props) => {
  const { exportToCSV } = useCSVExport({
    headers: CatalogCsvHeaders,
    headerIdDelimiter: '.',
  });

  const {
    selectedEnvId,
    selectedFolderAssetIds,
    selectedFolderAssetIdsHash,
    hasSelectedNodes,
    urlFiltersDebounced,
    urlSort,
    assetFilters,
    sort,
    filterOptions,
    isFilterOptionsFetching,
    isFilterOptionsError,
  } = useCatalogTableQueryContext();

  const {
    data: selectedAssetsData,
    isFetching: isSelectedAssetsFetching,
    isLoading: isSelectedAssetsLoading,
    isError: isAssetsError,
    fetchNextPage,
    hasNextPage,
    fetchAllPages,
  } = useGraphqlPagedQuery<
    CatalogTableAssetResponseItem,
    CatalogTableAssetPagedResponse
  >({
    queryKey: [
      CATALOG_TABLE_ASSETS,
      selectedEnvId,
      selectedFolderAssetIdsHash,
      urlFiltersDebounced,
      urlSort,
      search,
    ],
    query: getCatalogTableAssetsQuery,
    variables: {
      assetIds: selectedFolderAssetIds?.length ? selectedFolderAssetIds : null,
      search: search?.trim() ? search.trim() : null,
      assetFilters: assetFilters?.length ? assetFilters : null,
      sort,
      limit: PAGE_SIZE,
    },
    select: (data) => data.assetsPaged,
    enabled: enabled && hasSelectedNodes,
    envId: selectedEnvId,
  });

  const data = useMemo(() => {
    if (!hasSelectedNodes) return [];
    return parseAssets(selectedAssetsData);
  }, [selectedAssetsData, hasSelectedNodes]);

  const [isExporting, setIsExporting] = useState(false);

  const mapAssetsToCsvData = useCallback(
    (assets: CatalogTableAssetData[]) =>
      assets.map((r) => {
        const totals = getTestExecutionTotals(r.latestTestExecutions);
        const latestRunTime =
          r.latestTestExecutions?.[0]?.execution?.executionTimestamp;

        return {
          ...r,
          lastRunAt: latestRunTime
            ? localizeDate(latestRunTime).format('L LTS')
            : '',
          testResults: [
            totals?.passed > 0 ? `${totals?.passed} pass` : '',
            totals?.warnings > 0 ? `${totals?.warnings} warn` : '',
            totals?.errors > 0 || totals?.failures > 0
              ? `${(totals?.errors || 0) + (totals?.failures || 0)} err`
              : '',
          ]
            .filter(Boolean)
            .join(', '),
        };
      }),
    [],
  );

  const onCSVDownload = useCallback(async () => {
    setIsExporting(true);
    try {
      const allAssets = await fetchAllPages({ limit: EXPORT_PAGE_SIZE });
      const csvData = mapAssetsToCsvData(parseAssets(allAssets));
      exportToCSV(csvData as Record<string, any>[], 'catalog.csv');
    } finally {
      setIsExporting(false);
    }
  }, [fetchAllPages, mapAssetsToCsvData, exportToCSV]);

  return {
    data,
    filterOptions,
    onCSVDownload,
    isExporting,
    isAssetsFetching: isSelectedAssetsFetching || isSelectedAssetsLoading,
    isAssetsError: isAssetsError || isFilterOptionsError,
    isFilterOptionsFetching,
    fetchNextPage,
    hasNextPage,
  };
};
